import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { fmtDate, fmtTime, todayLocalIso } from '../../shared/dates';
import { ServiceLabels } from '@census/shared';

export function DailyAttendanceSheetPage({ print = false }: { print?: boolean }) {
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? todayLocalIso();

  const { data } = useQuery({
    queryKey: ['daily-sheet', date],
    queryFn: () => api.get('/instances/daily-sheet', { params: { date } }).then((r) => r.data),
  });
  const { data: settings } = useQuery({
    queryKey: ['clinic-info'],
    queryFn: () => api.get('/settings/clinic_info').then((r) => r.data),
  });

  useEffect(() => {
    if (print && data) setTimeout(() => window.print(), 350);
  }, [print, data]);

  if (!data) return <div className="p-6 text-sm">Loading…</div>;

  return (
    <div className="min-h-screen bg-white p-6 text-sm text-slate-900">
      <div className="no-print mb-4 flex flex-wrap items-center gap-3">
        <input
          type="date"
          className="input"
          value={date}
          onChange={(e) => setParams({ date: e.target.value })}
        />
        <button className="btn-primary" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <header className="mb-3 flex items-start justify-between border-b border-slate-300 pb-3">
        <div>
          <div className="text-base font-semibold">{settings?.clinicName ?? 'Clinic'}</div>
          <div className="text-xs text-slate-500">{settings?.clinicAddress}</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold">Daily Attendance Sheet</div>
          <div className="text-xs text-slate-500">{fmtDate(date)}</div>
        </div>
      </header>

      <section className="mb-4 grid grid-cols-4 gap-2 text-xs">
        <Stat label="Expected" value={data.totals.expected} />
        <Stat label="Attended" value={data.totals.attended} />
        <Stat label="No-show" value={data.totals.noShow} />
        <Stat label="Cancelled" value={data.totals.cancelled} />
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold">Group Sessions</h2>
        {data.groups.map((g: any) => (
          <div key={g.resourceId} className="mb-4 break-inside-avoid">
            <h3 className="mb-1 text-sm font-semibold text-slate-800">
              {g.displayName} · {fmtTime(g.startMin)}–{fmtTime(g.endMin)}
            </h3>
            <table className="w-full border-collapse text-xs">
              <thead className="bg-slate-100">
                <tr>
                  <th className="border border-slate-300 px-2 py-1 text-left">Cohort</th>
                  <th className="border border-slate-300 px-2 py-1 text-left">Client</th>
                  <th className="border border-slate-300 px-2 py-1 text-left">Status</th>
                  <th className="border border-slate-300 px-2 py-1 text-left">Check-in</th>
                  <th className="border border-slate-300 px-2 py-1 text-left">Late (min)</th>
                  <th className="border border-slate-300 px-2 py-1 text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r: any) => (
                  <tr key={r.id ?? r.clientId}>
                    <td className="border border-slate-300 px-2 py-1 font-mono">{r.cohortId}</td>
                    <td className="border border-slate-300 px-2 py-1">{r.name}</td>
                    <td className="border border-slate-300 px-2 py-1">{r.status}</td>
                    <td className="border border-slate-300 px-2 py-1">
                      {r.checkedInAt ? new Date(r.checkedInAt).toLocaleTimeString() : ''}
                    </td>
                    <td className="border border-slate-300 px-2 py-1">{r.lateMinutes ?? ''}</td>
                    <td className="border border-slate-300 px-2 py-1">
                      {r.noShowReason || r.cancellationReason || r.notes || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-1 text-[10px] text-slate-500">
              Facilitator signature: <span className="inline-block w-64 border-b border-slate-400" />
            </div>
          </div>
        ))}
        {data.groups.length === 0 && <p className="text-xs text-slate-500">No group sessions today.</p>}
      </section>

      <section className="mb-6 break-inside-avoid">
        <h2 className="mb-2 text-sm font-semibold">Individual Appointments</h2>
        <table className="w-full border-collapse text-xs">
          <thead className="bg-slate-100">
            <tr>
              <th className="border border-slate-300 px-2 py-1 text-left">Time</th>
              <th className="border border-slate-300 px-2 py-1 text-left">Cohort</th>
              <th className="border border-slate-300 px-2 py-1 text-left">Client</th>
              <th className="border border-slate-300 px-2 py-1 text-left">Service</th>
              <th className="border border-slate-300 px-2 py-1 text-left">Provider</th>
              <th className="border border-slate-300 px-2 py-1 text-left">Place</th>
              <th className="border border-slate-300 px-2 py-1 text-left">Status</th>
              <th className="border border-slate-300 px-2 py-1 text-left">Notes</th>
            </tr>
          </thead>
          <tbody>
            {data.individual.map((i: any) => (
              <tr key={i.id}>
                <td className="border border-slate-300 px-2 py-1">{fmtTime(i.startMin)}–{fmtTime(i.endMin)}</td>
                <td className="border border-slate-300 px-2 py-1 font-mono">{i.cohortId}</td>
                <td className="border border-slate-300 px-2 py-1">{i.name}</td>
                <td className="border border-slate-300 px-2 py-1">{ServiceLabels[i.serviceType as 'THERAPY']}</td>
                <td className="border border-slate-300 px-2 py-1">{i.providerName ?? ''}</td>
                <td className="border border-slate-300 px-2 py-1">{i.placeOfService}</td>
                <td className="border border-slate-300 px-2 py-1">{i.status}</td>
                <td className="border border-slate-300 px-2 py-1">
                  {i.noShowReason || i.cancellationReason || i.notes || ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.individual.length === 0 && (
          <p className="text-xs text-slate-500">No individual appointments today.</p>
        )}
      </section>

      <footer className="mt-8 grid grid-cols-2 gap-12 text-xs">
        <div>
          Reviewed by: <span className="ml-2 inline-block w-48 border-b border-slate-400" /> Date:{' '}
          <span className="ml-2 inline-block w-24 border-b border-slate-400" />
        </div>
        <div>
          Supervisor signature: <span className="ml-2 inline-block w-48 border-b border-slate-400" /> Date:{' '}
          <span className="ml-2 inline-block w-24 border-b border-slate-400" />
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-slate-300 p-2 text-center">
      <div className="text-xl font-semibold">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}
